<template>
    <div class="mt-4">

        <!-- Shipping -->
        <vx-card cardBorder noShadow title="Shipping" class="mb-3">
            <div class="vx-row mb-3">
                <div class="vx-col w-1/2">
                    <small class="label">Pickup Requested?</small>
                    <vs-switch v-model="form.pickup.isRequested" />
                </div>
                <div class="vx-col w-1/2">
                    <small class="label">Planned Shipping Date & Time</small>
                    <flat-pickr :config="{ enableTime: true, dateFormat: 'Y-m-d H:i:S' }" class="w-full"
                        v-model="form.plannedShippingDateAndTime" placeholder="Choose Date & Time" />
                </div>
            </div>
        </vx-card>

        <!-- Product -->
        <vx-card cardBorder noShadow title="Product" class="mb-3 hidden">
            <div class="vx-row mb-3">
                <div class="vx-col w-1/2">
                    <small class="label">Product Code</small>
                    <vs-input v-model="form.productCode" class="w-full" />
                </div>
            </div>
        </vx-card>

        <!-- Accounts -->
        <vx-card cardBorder noShadow title="DHL Express Accounts" class="mb-3">
            <div class="vx-row mb-3" v-for="(account, index) in form.accounts" :key="'account-' + index">
                <div class="vx-col w-1/2">
                    <small class="label">Type Code</small>
                    <vs-input v-model="account.typeCode" class="w-full" />
                </div>
                <div class="vx-col w-1/2">
                    <small class="label">Account Number</small>
                    <vs-input v-model="account.number" class="w-full" />
                </div>
            </div>
        </vx-card>

        <!-- Shipper -->
        <vx-card cardBorder noShadow title="Shipper Details" class="mb-3">
            <div class="vx-row mb-3" v-if="shipping_addresses.length > 1">
                <div class="vx-col w-full">
                    <small class="label">Select Shipping Address</small>
                    <v-select
                        :options="shipping_addresses"
                        :reduce="option => option"
                        label="name"
                        v-model="selected_shipping_address"
                        @input="setShipperAddress"
                        :clearable="false"
                        :dir="$vs.rtl ? 'rtl' : 'ltr'">
                        <template slot="option" slot-scope="option">
                            <div>
                                <span style="font-weight: 500">{{ option.name }}</span>
                                <span class="text-sm text-grey-dark" v-if="option.line1">, {{ option.line1 }}</span>
                                <span class="text-sm text-grey-dark" v-if="option.town_state">, {{ option.town_state }}</span>
                                <span class="text-sm text-grey-dark" v-if="option.post_code"> ({{ option.post_code }})</span>
                                <span v-if="option.is_default" class="text-xs text-primary ml-1">(Default)</span>
                            </div>
                        </template>
                        <template slot="selected-option" slot-scope="option">
                            <div>
                                <span style="font-weight: 500">{{ option.name }}</span>
                                <span v-if="option.line1">, {{ option.line1 }}</span>
                                <span v-if="option.town_state">, {{ option.town_state }}</span>
                                <span v-if="option.post_code"> ({{ option.post_code }})</span>
                            </div>
                        </template>
                    </v-select>
                </div>
            </div>

            <div class="vx-row mb-3">
                <div class="vx-col w-1/2">
                    <small class="label">Company Name</small>
                    <vs-input v-model="form.customerDetails.shipperDetails.contactInformation.companyName"
                        class="w-full" />
                </div>
                <div class="vx-col w-1/2">
                    <small class="label">Full Name</small>
                    <vs-input v-model="form.customerDetails.shipperDetails.contactInformation.fullName"
                        class="w-full" />
                </div>
            </div>

            <div class="vx-row mb-3">
                <div class="vx-col w-1/2">
                    <small class="label">Email</small>
                    <vs-input v-model="form.customerDetails.shipperDetails.contactInformation.email" class="w-full" />
                </div>
                <div class="vx-col w-1/2">
                    <small class="label">Phone</small>
                    <vs-input v-model="form.customerDetails.shipperDetails.contactInformation.phone" class="w-full" />
                </div>
            </div>

            <div class="vx-row mb-3">
                <div class="vx-col w-1/2">
                    <small class="label">Address Line</small>
                    <vs-input v-model="form.customerDetails.shipperDetails.postalAddress.addressLine1" class="w-full" />
                </div>
                <div class="vx-col w-1/4">
                    <small class="label">City</small>
                    <vs-input v-model="form.customerDetails.shipperDetails.postalAddress.cityName" class="w-full" />
                </div>
                <div class="vx-col w-1/4">
                    <small class="label">Postal Code</small>
                    <vs-input v-model="form.customerDetails.shipperDetails.postalAddress.postalCode" class="w-full" />
                </div>
            </div>

        </vx-card>

        <!-- Receiver -->
        <vx-card cardBorder noShadow title="Receiver Details" class="mb-3 hidden">
            <div class="vx-row mb-3">
                <div class="vx-col w-1/2">
                    <small class="label">Company Name</small>
                    <vs-input v-model="form.customerDetails.receiverDetails.contactInformation.companyName"
                        class="w-full" />
                </div>
                <div class="vx-col w-1/2">
                    <small class="label">Full Name</small>
                    <vs-input v-model="form.customerDetails.receiverDetails.contactInformation.fullName"
                        class="w-full" />
                </div>
            </div>

            <div class="vx-row mb-3">
                <div class="vx-col w-1/2">
                    <small class="label">Email</small>
                    <vs-input v-model="form.customerDetails.receiverDetails.contactInformation.email" class="w-full" />
                </div>
                <div class="vx-col w-1/2">
                    <small class="label">Phone</small>
                    <vs-input v-model="form.customerDetails.receiverDetails.contactInformation.phone" class="w-full" />
                </div>
            </div>

        </vx-card>

        <!-- Package -->
        <vx-card cardBorder noShadow title="Package" class="mb-3 hidden">
            <div class="vx-row mb-3" v-for="(pkg, index) in form.content.packages" :key="'pkg-' + index">
                <div class="vx-col w-1/4">
                    <small class="label">Weight (kg)</small>
                    <vs-input type="number" v-model="pkg.weight" class="w-full" />
                </div>
                <div class="vx-col w-1/4">
                    <small class="label">Length</small>
                    <vs-input type="number" v-model="pkg.dimensions.length" class="w-full" />
                </div>
                <div class="vx-col w-1/4">
                    <small class="label">Width</small>
                    <vs-input type="number" v-model="pkg.dimensions.width" class="w-full" />
                </div>
                <div class="vx-col w-1/4">
                    <small class="label">Height</small>
                    <vs-input type="number" v-model="pkg.dimensions.height" class="w-full" />
                </div>
            </div>
        </vx-card>

        <!-- Customs -->
        <!-- <vx-card cardBorder noShadow title="Customs" class="mb-3">
            <div class="vx-row mb-3">
                <div class="vx-col w-1/2">
                    <small class="label">Customs Declarable?</small>
                    <vs-switch v-model="form.content.isCustomsDeclarable" />
                </div>
                <div class="vx-col w-1/4">
                    <small class="label">Declared Value</small>
                    <vs-input type="number" v-model="form.content.declaredValue" class="w-full" />
                </div>
                <div class="vx-col w-1/4">
                    <small class="label">Currency</small>
                    <vs-input v-model="form.content.declaredValueCurrency" class="w-full" />
                </div>
            </div>

        </vx-card> -->

        <!-- Export Line Items -->
        <!-- <vx-card cardBorder noShadow title="Export Line Items" class="mb-3">
            <div class="vx-row mb-3" v-for="(item, index) in form.content.exportDeclaration.lineItems"
                :key="'line-' + index">
                <div class="vx-col w-1/2">
                    <small class="label">Description</small>
                    <vs-input v-model="item.description" class="w-full" />
                </div>
                <div class="vx-col w-1/4">
                    <small class="label">Price</small>
                    <vs-input type="number" v-model="item.price" class="w-full" />
                </div>
                <div class="vx-col w-1/4">
                    <small class="label">Quantity</small>
                    <vs-input type="number" v-model="item.quantity.value" class="w-full" />
                </div>
            </div>
        </vx-card> -->

        <!-- Value Added Services / Dangerous Goods -->
        <vx-card cardBorder noShadow title="DHL Value Added Services (Dangerous Goods / Special Services)" class="mb-3">
            <div class="vx-row mb-3">
                <div class="vx-col w-full">
                    <small class="label">Special Services / Dangerous Goods Declarations</small>
                    <v-select
                        multiple
                        v-model="selectedServiceCodes"
                        :options="valueAddedServiceOptions"
                        :reduce="option => option.code"
                        label="label"
                        placeholder="Select DHL Value Added Services (e.g. HE, HV, HD, HC...)"
                        :dir="$vs.rtl ? 'rtl' : 'ltr'"
                    />
                </div>
            </div>
            <div v-if="selectedServiceCodes && selectedServiceCodes.length > 0" class="vx-row mb-2">
                <div class="vx-col w-full">
                    <small class="label text-muted">Selected Services:</small>
                    <div class="flex flex-wrap gap-2 mt-1">
                        <vs-chip v-for="code in selectedServiceCodes" :key="code" closable @click="removeServiceCode(code)" color="primary">
                            {{ getServiceLabel(code) }} ({{ code }})
                        </vs-chip>
                    </div>
                </div>
            </div>
        </vx-card>

        <!-- Invoice -->
        <vx-card cardBorder noShadow title="Invoice" class="mb-3" v-if="isCustomsDeclarable"> 
            <div class="vx-row mb-3">
                <div class="vx-col w-1/2">
                    <small class="label">Invoice Number</small>
                    <vs-input v-model="form.content.exportDeclaration.invoice.number" class="w-full" />
                </div>
                <div class="vx-col w-1/2">
                    <small class="label">Invoice Date</small>
                    <flat-pickr :config="{ dateFormat: 'Y-m-d' }" class="w-full"
                        v-model="form.content.exportDeclaration.invoice.date" />
                </div>
            </div>
        </vx-card>

        <vs-button color="primary" type="border" class="hidden">Check Rates</vs-button>


    </div>
</template>

<script>
import flatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css'
import vSelect from 'vue-select'

import { RepositoryFactory } from '@/repository/RepositoryFactory'
import ErrorMessages from '@/mixins/ErrorMessages'
const SettingsRepository = RepositoryFactory.get('settings');
const CompanyAddressRepository = RepositoryFactory.get('company_address');

export default {
    name: "DhlShipmentForm",
    mixins: [ErrorMessages],
    data() {
        return {
            shipping_addresses: [],
            selected_shipping_address: null,
            selectedServiceCodes: [],
            valueAddedServiceOptions: [
                { code: 'HE', label: 'Dangerous Goods (Associated DGD - PAX/CAO)' },
                { code: 'HL', label: 'Limited Quantities ADR' },
                { code: 'HK', label: 'Consumer Goods ID8000' },
                { code: 'HN', label: 'ADR Load Exemptions' },
                { code: 'HY', label: 'Biological Substances UN3373 / GMO' },
                { code: 'HC', label: 'Dry Ice UN1845' },
                { code: 'HH', label: 'Excepted Quantities (IATA)' },
                { code: 'HD', label: 'Lithium Ion PI966 Section II (with equipment)' },
                { code: 'HV', label: 'Lithium Ion PI967 Section II (in equipment)' },
                { code: 'HM', label: 'Lithium Metal PI969 Section II (with equipment)' },
                { code: 'HW', label: 'Lithium Metal PI970 Section II (in equipment)' },
                { code: 'HX', label: 'Magnetized Material UN2807' },
                { code: 'HU', label: 'Not Restricted Dangerous Goods / Exempt Specimens' },
            ],
            form: {
                "plannedShippingDateAndTime": "",
                "pickup": {
                    "isRequested": true
                },
                "productCode": "",
                "valueAddedServices": [],
                "accounts": [
                    {
                        "typeCode": "shipper",
                        "number": "418028739"
                    }
                ],
                "customerDetails": {
                    "shipperDetails": {
                        "postalAddress": {
                            "postalCode": "",
                            "cityName": "",
                            "countryCode": "",
                            "addressLine1": ""
                        },
                        "contactInformation": {
                            "email": "",
                            "phone": "",
                            "companyName": "",
                            "fullName": ""
                        }
                    },
                    "receiverDetails": {
                        "postalAddress": {
                            "postalCode": "",
                            "cityName": "",
                            "countryCode": "",
                            "addressLine1": ""
                        },
                        "contactInformation": {
                            "email": "",
                            "phone": "",
                            "companyName": "",
                            "fullName": ""
                        }
                    }
                },
                "content": {
                    "packages": [
                        {
                            "weight": 0,
                            "dimensions": {
                                "length": 0,
                                "width": 0,
                                "height": 0
                            }
                        }
                    ],
                    "isCustomsDeclarable": false,
                    // "declaredValue": 0,
                    "declaredValueCurrency": "GBP",
                    "exportDeclaration": {
                        "lineItems": [
                            {
                                "number": 1,
                                "description": "",
                                "price": 100,
                                "quantity": {
                                    "value": 1,
                                    "unitOfMeasurement": "KG"
                                },
                                "commodityCodes": [],
                                "manufacturerCountry": "",
                                "weight": {
                                    "netValue": 0,
                                    "grossValue": 0
                                }
                            }
                        ],
                        "invoice": {
                            "number": "",
                            "date": ""
                        }
                    },
                    "description": "",
                    "incoterm": "",
                    "unitOfMeasurement": "metric"
                }
            }
        }
    },
    props: {
        shipment_form: {
            default: () => ({}),
            type: Object,
            required: true
        },
        invoices: {
            default: () => ([]),
            type: Array
        }
    },
    components: {
        flatPickr,
        'v-select': vSelect
    },
    async mounted() {
        await this.fetchAddress()
        this.autoFillInvoice()
        this.syncDgServicesFromShipmentForm()
    },
    methods: {
        removeServiceCode(code) {
            this.selectedServiceCodes = this.selectedServiceCodes.filter(c => c !== code)
        },
        getServiceLabel(code) {
            const item = this.valueAddedServiceOptions.find(opt => opt.code === code)
            return item ? item.label : code
        },
        syncDgServicesFromShipmentForm() {
            if (!this.shipment_form) return
            if (this.shipment_form.is_hazardous) {
                if (!this.selectedServiceCodes.includes('HE')) {
                    this.selectedServiceCodes = [...this.selectedServiceCodes.filter(c => c !== 'HL'), 'HE']
                }
            } else if (this.shipment_form.dangerous_goods_details_arr && this.shipment_form.dangerous_goods_details_arr.length > 0) {
                if (!this.selectedServiceCodes.includes('HL')) {
                    this.selectedServiceCodes = [...this.selectedServiceCodes.filter(c => c !== 'HE'), 'HL']
                }
            }
        },
        autoFillInvoice() {
            if (this.invoices && this.invoices.length > 0) {
                let selectedInvoice = null
                if (this.shipment_form && this.shipment_form.product_order_invoice_id && this.shipment_form.product_order_invoice_id.length > 0) {
                    const firstId = this.shipment_form.product_order_invoice_id[0]
                    selectedInvoice = this.invoices.find(inv => inv.id === firstId) || this.invoices[0]
                } else {
                    selectedInvoice = this.invoices[0]
                }

                if (selectedInvoice) {
                    if (selectedInvoice.number) {
                        this.form.content.exportDeclaration.invoice.number = selectedInvoice.number
                    }
                    if (selectedInvoice.date) {
                        this.form.content.exportDeclaration.invoice.date = selectedInvoice.date
                    }
                }
            }
        },
        setShipperAddress(address) {
            if (!address) return
            this.selected_shipping_address = address
            this.form.customerDetails.shipperDetails.contactInformation.companyName = address.name || ''
            this.form.customerDetails.shipperDetails.contactInformation.fullName = address.name || ''
            this.form.customerDetails.shipperDetails.postalAddress.addressLine1 = address.line1 || ''
            this.form.customerDetails.shipperDetails.postalAddress.cityName = address.town_state || ''
            this.form.customerDetails.shipperDetails.postalAddress.countryCode = address.country || ''
            this.form.customerDetails.shipperDetails.postalAddress.postalCode = address.post_code || ''
        },
        async fetchAddress() {
            try {
                try {
                    const settings = (await SettingsRepository.getTheItem('telephone,sales_contact_email')).data.data
                    this.form.customerDetails.shipperDetails.contactInformation.email = settings.sales_contact_email || ''
                    this.form.customerDetails.shipperDetails.contactInformation.phone = settings.telephone || ''

                    const { data: addresses } = (await CompanyAddressRepository.getCompanyAddresses('filter[is_shipping_address]=1')).data
                    this.shipping_addresses = addresses || []

                    if (this.shipping_addresses.length > 0) {
                        const defaultShipping = this.shipping_addresses.find(addr => addr.is_default) || this.shipping_addresses[0]
                        this.setShipperAddress(defaultShipping)
                    }
                } catch (e) {
                    this.convertAndNotifyError(e)
                }
            } catch (error) {

            }
        }
    },
    watch: {
        selectedServiceCodes: {
            deep: true,
            immediate: true,
            handler(val) {
                this.form.valueAddedServices = (val || []).map(code => ({ serviceCode: code }))
            }
        },
        'shipment_form.is_hazardous': {
            handler() {
                this.syncDgServicesFromShipmentForm()
            }
        },
        'shipment_form.dangerous_goods_details_arr': {
            deep: true,
            handler() {
                this.syncDgServicesFromShipmentForm()
            }
        },
        form: {
            deep: true,
            handler(val) {
                this.$emit("update-form", val)
            }
        },
        invoices: {
            deep: true,
            handler() {
                this.autoFillInvoice()
            }
        },
        'shipment_form.product_order_invoice_id': {
            deep: true,
            handler() {
                this.autoFillInvoice()
            }
        }
    },
    computed :{
        isCustomsDeclarable() {
            let destination_country = this.shipment_form.delivery_address.country
            let shipping_country = this.form.customerDetails.shipperDetails.postalAddress.countryCode
            if (destination_country === 'United Kingdom') {
                destination_country = 'Great Britain'
            }
            if (shipping_country === 'United Kingdom') {
                shipping_country = 'Great Britain'
            }
            return shipping_country !== destination_country
        }
    }
}
</script>
<style lang="">

</style>